import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { messageService } from '../utils/messageService';

const MessageContext = createContext();

export function MessageProvider({ children }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!user) return;

    let retryCount = 0;
    const maxRetries = 3;
    let retryTimeout;

    // Initialize SignalR connection
    const initializeMessaging = async () => {
      try {
        const token = localStorage.getItem('velaToken');
        const success = await messageService.initialize(user.sub, token);
        
        if (!success && retryCount < maxRetries) {
          retryCount++;
          retryTimeout = setTimeout(initializeMessaging, 5000);
        }
      } catch (error) {
        if (retryCount < maxRetries) {
          retryCount++;
          retryTimeout = setTimeout(initializeMessaging, 5000);
        }
      }
    };

    initializeMessaging();

    // Set up event listeners
    messageService.on('messageReceived', handleMessageReceived);
    messageService.on('userTyping', handleUserTyping);
    messageService.on('userStatusChanged', handleUserStatusChanged);
    messageService.on('connectionStateChanged', (state) => {
      setIsConnected(state === 'connected');
    });
    messageService.on('messagesLoaded', (loadedMessages) => {
      // Sort messages by timestamp (oldest first to match display)
      const sortedMessages = loadedMessages
        .map(m => ({
          ...m,
          // Ensure UTC timestamp
          timestamp: m.timestamp.endsWith('Z') ? 
            new Date(m.timestamp).toISOString() : 
            new Date(m.timestamp + 'Z').toISOString()
        }))
        .sort((a, b) => {
          const timeA = new Date(a.timestamp).getTime();
          const timeB = new Date(b.timestamp).getTime();
          return timeA - timeB;
        });
      
      setMessages(sortedMessages);
      // Update unread count
      const unreadMessages = sortedMessages.filter(m => 
        m.senderId !== user.sub && !m.read
      ).length;
      setUnreadCount(unreadMessages);
    });

    // Cleanup
    return () => {
      messageService.off('messageReceived', handleMessageReceived);
      messageService.off('userTyping', handleUserTyping);
      messageService.off('userStatusChanged', handleUserStatusChanged);
      messageService.off('connectionStateChanged');
      messageService.off('messagesLoaded');
      messageService.disconnect();
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [user]);

  const handleMessageReceived = (message) => {
    if (!message) return;
    
    setMessages(prev => {
      // Check if message already exists - only consider exact matches
      const exists = prev.some(m => 
        // If we have IDs, use them for comparison
        (m.id && message.id && m.id === message.id) ||
        // Otherwise, check all properties
        (m.senderId === message.senderId && 
         m.recipientId === message.recipientId && 
         m.content === message.content && 
         m.timestamp === message.timestamp)
      );
      
      if (exists) return prev;
      
      // Add new message and ensure UTC timestamp
      const newMessage = {
        ...message,
        timestamp: message.timestamp.endsWith('Z') ? 
          new Date(message.timestamp).toISOString() : 
          new Date(message.timestamp + 'Z').toISOString()
      };
      
      // Add new message and sort by timestamp
      const newMessages = [...prev, newMessage].sort((a, b) => {
        const timeA = new Date(a.timestamp).getTime();
        const timeB = new Date(b.timestamp).getTime();
        return timeA - timeB;
      });
      
      return newMessages;
    });
    
    // Update unread count if message is not from current user
    if (message.senderId !== user?.sub) {
      setUnreadCount(prev => prev + 1);
    }
  };

  const handleUserTyping = (userId) => {
    setTypingUsers(prev => new Set(prev).add(userId));
    // Clear typing indicator after 3 seconds
    setTimeout(() => {
      setTypingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }, 3000);
  };

  const handleUserStatusChanged = ({ userId, isOnline }) => {
    setOnlineUsers(prev => {
      const newSet = new Set(prev);
      if (isOnline) {
        newSet.add(userId);
      } else {
        newSet.delete(userId);
      }
      return newSet;
    });
  };

  const sendMessage = async (recipientId, content) => {
    const success = await messageService.sendMessage(recipientId, content);
    if (success) {
      // Message will be added through the messageReceived handler with server timestamp
      console.log('Message sent successfully, waiting for server confirmation');
    }
    return success;
  };

  const sendTypingIndicator = (recipientId) => {
    messageService.sendTypingIndicator(recipientId);
  };

  const markAsRead = (messageIds) => {
    setUnreadCount(prev => Math.max(0, prev - messageIds.length));
    // You might want to persist this to your backend as well
  };

  const clearMessages = () => {
    setMessages([]);
    setUnreadCount(0);
  };

  const value = {
    messages,
    typingUsers,
    onlineUsers,
    unreadCount,
    sendMessage,
    sendTypingIndicator,
    markAsRead,
    clearMessages,
    setMessages,
    isConnected
  };

  return (
    <MessageContext.Provider value={value}>
      {children}
    </MessageContext.Provider>
  );
}

export function useMessages() {
  const context = useContext(MessageContext);
  console.log('Messages:', context.messages);
  if (!context) {
    throw new Error('useMessages must be used within a MessageProvider');
  }
  return context;
} 